import { Tooltip } from '@wheely/ui-kit';
import React, { useMemo } from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber } from 'react-intl';

import { IBill } from '@/modules/bill/model';
import { EntityPage } from '@/shared/models/entity-page';
import { IColumnConfig } from '@/shared/view/ListLayout/List';
import { ListLayoutView } from '@/shared/view/ListLayout/ListLayout';
import { MobileListItem } from '@/shared/view/ListLayout/MobileListItem';
import { Icon } from '@src-common/view/components/Icon';

import { UpcomingBill } from './components/UpcomingBill';
import s from './styles.module.css';

type Props = {
  upcomingBill: IBill | null;
  bills: EntityPage<IBill>;
  fetchBills: (_: any, pagination: { offset: number; limit: number }) => void;
  timezone?: string;
  isLoading?: boolean;
};

const View = ({ upcomingBill, bills, fetchBills, timezone, isLoading = false }: Props) => {
  const columns: IColumnConfig<IBill>[] = useMemo(
    () => [
      {
        renderHeader: () => <></>,
        renderCell: bill => (
          <Tooltip
            content={
              bill.isPaid ? (
                <FormattedMessage
                  defaultMessage="Successful payment"
                  id="QGgE/h"
                  description="Payment state: successful"
                />
              ) : (
                <FormattedMessage
                  defaultMessage="Failed payment"
                  id="24QX5b"
                  description="Payment state: failed"
                />
              )
            }
          >
            <div className={s.iconWrapper}>
              <Icon
                icon={bill.isPaid ? 'checkmark' : 'cross'}
                className={bill.isPaid ? s.iconCheckmark : s.iconCross}
              />
            </div>
          </Tooltip>
        ),
        style: { width: '44px' },
      },
      {
        renderHeader: () => (
          <FormattedMessage
            defaultMessage="Number"
            id="eyTih+"
            description="Bills table: Number column header"
          />
        ),
        renderCell: bill => bill.number,
        style: { width: '110px' },
      },
      {
        renderHeader: () => (
          <FormattedMessage
            defaultMessage="Date"
            id="R9QjGW"
            description="Bills table: Date column header"
          />
        ),
        renderCell: bill => <FormattedDate value={bill.date} timeZone={timezone} />,
      },
      {
        renderHeader: () => (
          <FormattedMessage
            defaultMessage="Paid at"
            id="STXGoD"
            description="Bills table: Paid at column header"
          />
        ),
        renderCell: bill =>
          bill.paidAtDate ? <FormattedDate value={bill.paidAtDate} timeZone={timezone} /> : '–',
      },
      {
        renderHeader: () => (
          <FormattedMessage
            defaultMessage="Period"
            id="fDE6wL"
            description="Bills table: Period column header"
          />
        ),
        renderCell: bill => (
          <>
            <FormattedDate value={bill.periodStart} timeZone={timezone} />
            {' – '}
            <FormattedDate value={bill.periodEnd} timeZone={timezone} />
          </>
        ),
        style: { width: '184px' },
      },
      {
        renderHeader: () => (
          <FormattedMessage
            defaultMessage="Amount"
            id="m186q+"
            description="Bills table: Amount column header"
          />
        ),
        renderCell: bill => (
          <FormattedNumber value={bill.amount} style="currency" currency={bill.currencyCode} />
        ),
      },
      {
        renderHeader: () => <></>,
        renderCell: bill => (
          <div className={s.links}>
            <a className={s.link} href={bill.pdfUrl} target="_blank" rel="noreferrer">
              PDF
            </a>
            <a className={s.link} href={bill.excelUrl} target="_blank" rel="noreferrer">
              XLS
            </a>
          </div>
        ),
        style: { width: '80px' },
      },
    ],
    [timezone],
  );

  return (
    <div className={s.root}>
      <ListLayoutView<IBill, ''>
        title={
          <FormattedMessage defaultMessage="Bills" id="Yhdu1X" description="Bills page title" />
        }
        data={bills.items}
        before={
          upcomingBill ? (
            <UpcomingBill
              amount={upcomingBill.amount}
              currencyCode={upcomingBill.currencyCode}
              periodStart={upcomingBill.periodStart}
              periodEnd={upcomingBill.periodEnd}
              timezone={timezone}
            />
          ) : null
        }
        after={<div className={s.bottomPadding} />}
        total={bills.total}
        limit={bills.limit}
        columns={columns}
        getEntityId={bill => String(bill.id)}
        onFetchData={fetchBills}
        shouldBeEmptyWithoutFilters={false}
        isLoading={isLoading}
        renderItem={bill => (
          <MobileListItem
            title={<FormattedDate value={bill.date} timeZone={timezone} />}
            note={
              <>
                <FormattedDate value={bill.periodStart} timeZone={timezone} />
                {' – '}
                <FormattedDate value={bill.periodEnd} timeZone={timezone} />
              </>
            }
            left={
              <div className={s.mobileIconWrapper}>
                <Icon
                  icon={bill.isPaid ? 'checkmark' : 'cross'}
                  className={bill.isPaid ? s.iconCheckmark : s.iconCross}
                />
              </div>
            }
            right={
              <div className={s.mobileRight}>
                <FormattedNumber
                  value={bill.amount}
                  style="currency"
                  currency={bill.currencyCode}
                />
                <a className={s.link} href={bill.pdfUrl} target="_blank" rel="noreferrer">
                  PDF
                </a>
              </div>
            }
          />
        )}
      />
    </div>
  );
};

export { View };
